import React, { useState, useEffect, useRef, useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import FireTruckOutlinedIcon from '@mui/icons-material/FireTruckOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { COLORS, SOFT_SHADOW } from '../../view/frete-copy/css/formFields';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import firebase from '../../config/firebase';
import freightApi2 from '../../service/modules/freight.api';
import DialogFilaTransport, {
  parseTransportQueueResponse,
} from '../dialog-fila-transport/dialog-fila-transport';
import {
  cancelFreightById,
  getCancelFreightErrorMessage,
} from '../../util/freight/cancelFreight';
import {
  acceptTransportFromQueue,
  getAcceptTransportErrorMessage,
} from './acceptTransportFromQueue';
import {
  buildTransportQueueNotification,
  isFreightPendingContract,
  isTransportQueueNotificationRead,
  mapApiFreightForNotification,
  markTransportQueueNotificationRead,
  parseFreightsV2Items,
  resolveShipperUidFromFreight,
  TRANSPORT_QUEUE_FREIGHT_STATUSES,
} from './transportQueueNotification.utils';

const TRANSPORT_QUEUE_POLL_MS = 60 * 1000;
/** Evita competir com GET /freight/v2 da listagem no mesmo segundo da navegação. */
const TRANSPORT_QUEUE_INITIAL_DELAY_MS = 8 * 1000;

function toNotificationDate(date) {
  if (!date) return new Date(0);
  return date.toDate ? date.toDate() : new Date(date);
}

function extractFreightNumberFromText(text) {
  const match = String(text || '').match(/#\s*(\d+)/);
  return match ? match[1] : '';
}

function getMessageFreightId(data) {
  return data.uidFrete || data.freightId || data.idFreight || null;
}

function getMessageFreightNumber(data, title, description) {
  return (
    data.numberSerial ||
    data.freightNumber ||
    extractFreightNumberFromText(title) ||
    extractFreightNumberFromText(description) ||
    ''
  );
}

function mapMessageDocToNotification(userWebDocId, messageDoc) {
  const data = messageDoc.data();
  const isRead = data.isRead === true || data.read === true;

  if (isRead) return null;

  const title =
    data.title || data.subject || data.titulo || 'Nova mensagem';
  const description =
    data.message ||
    data.body ||
    data.content ||
    data.text ||
    data.description ||
    data.mensagem ||
    '';
  const freightId = getMessageFreightId(data);
  const freightNumber = getMessageFreightNumber(data, title, description);

  return {
    id: `message_${userWebDocId}_${messageDoc.id}`,
    type: 'message',
    userWebDocId,
    messageDocId: messageDoc.id,
    freightId,
    freightNumber: freightNumber || freightId || '',
    title,
    description,
    date:
      data.createdAt ||
      data.timestamp ||
      data.date ||
      data.createData ||
      new Date(),
    read: false,
  };
}

const modalInfoCardSx = {
  borderRadius: '16px',
  bgcolor: COLORS.white,
  border: `1px solid ${COLORS.line}`,
  boxShadow: SOFT_SHADOW,
  p: 2,
};

const modalSectionLabelSx = {
  fontSize: '12px',
  fontWeight: 700,
  color: COLORS.inkSoft,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  mb: 0.5,
  display: 'block',
};

const modalValueSx = {
  fontSize: '16px',
  fontWeight: 600,
  color: COLORS.ink,
};

function isFreightPendingValidation(freightData) {
  const describe = freightData?.status?.describe || '';
  const code = String(freightData?.status?.code ?? '');
  return (
    describe === 'P. Validação' ||
    describe === 'Pendente de validação' ||
    code === '00'
  );
}

export default function NotificationMenu() {
  const user = useSelector((state) => state.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [queueNotifications, setQueueNotifications] = useState([]);
  const [transportQueueNotifications, setTransportQueueNotifications] = useState(
    [],
  );
  const [messageNotifications, setMessageNotifications] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isPrincipalShipper, setIsPrincipalShipper] = useState(false);
  const [userWebDocId, setUserWebDocId] = useState(null);
  const [responsibleShipperUids, setResponsibleShipperUids] = useState([]);
  const [transportModalOpen, setTransportModalOpen] = useState(false);
  const [transportModalFreightId, setTransportModalFreightId] = useState(null);
  const [transportModalShipperUid, setTransportModalShipperUid] = useState(null);
  const [transportModalFreightStatus, setTransportModalFreightStatus] = useState(null);
  const [transportsInFila, setTransportsInFila] = useState([]);
  const [acceptingTransportId, setAcceptingTransportId] = useState(null);
  const [cancelFreightLoading, setCancelFreightLoading] = useState(false);
  const [transportActionMessage, setTransportActionMessage] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const unsubscribeRefs = useRef([]);
  const shipperIdsBySellerRef = useRef({});

  const cleanupQueueListeners = () => {
    unsubscribeRefs.current.forEach((unsubscribe) => {
      if (unsubscribe) unsubscribe();
    });
    unsubscribeRefs.current = [];
  };

  const syncTransportNotificationsForFreight = (
    freightId,
    shipperUid,
    freightData,
    queue,
  ) => {
    const unreadQueue = queue.filter(
      (item) =>
        item.idTransport &&
        !isTransportQueueNotificationRead(freightId, item.idTransport),
    );

    setTransportQueueNotifications((prev) => {
      const filtered = prev.filter(
        (notif) =>
          notif.type !== 'transportQueue' || notif.freightId !== freightId,
      );

      const next = unreadQueue.map((item) =>
        buildTransportQueueNotification({
          freightId,
          freightData,
          transportItem: item,
          shipperUid,
        }),
      );

      return [...filtered, ...next];
    });
  };

  const refreshTransportQueueForFreight = async (
    freightId,
    shipperUid,
    freightData,
  ) => {
    if (!freightId || !shipperUid) return;

    try {
      const res = await freightApi2.transportQueue(freightId, shipperUid);
      const queue = parseTransportQueueResponse(res);
      syncTransportNotificationsForFreight(
        freightId,
        shipperUid,
        freightData,
        queue,
      );
    } catch (error) {
      console.error('Erro ao buscar fila de transportadoras:', {
        freightId,
        shipperUid,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const checkPrincipalShipper = async () => {
      if (!user?.uidShipper) {
        if (!cancelled) setIsPrincipalShipper(false);
        return;
      }

      try {
        const snap = await firebase
          .firestore()
          .collection('shipper')
          .doc(user.uidShipper)
          .get();

        if (!cancelled) {
          setIsPrincipalShipper(
            snap.exists && snap.data()?.isPrincipal === true,
          );
        }
      } catch (error) {
        console.error('Erro ao verificar isPrincipal do embarcador:', error);
        if (!cancelled) setIsPrincipalShipper(false);
      }
    };

    checkPrincipalShipper();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user?.uid) {
      setMessageNotifications([]);
      return undefined;
    }

    const db = firebase.firestore();
    let cancelled = false;
    let unsubscribeMessages = null;

    const setupMessageListener = async () => {
      try {
        const userSnap = await db
          .collection('user_web')
          .where('uid', '==', user.uid)
          .limit(1)
          .get();

        if (cancelled) return;

        if (userSnap.empty) {
          setUserWebDocId(null);
          setMessageNotifications([]);
          return;
        }

        const resolvedUserWebDocId = userSnap.docs[0].id;
        if (!cancelled) setUserWebDocId(resolvedUserWebDocId);

        unsubscribeMessages = db
          .collection('user_web')
          .doc(resolvedUserWebDocId)
          .collection('message')
          .onSnapshot(
            (messageSnapshot) => {
              const items = messageSnapshot.docs
                .map((doc) =>
                  mapMessageDocToNotification(resolvedUserWebDocId, doc),
                )
                .filter(Boolean);

              setMessageNotifications(items);
            },
            (error) => {
              console.error('Erro ao buscar mensagens do usuário:', error);
            },
          );
      } catch (error) {
        console.error('Erro ao configurar listener de mensagens:', error);
        if (!cancelled) {
          setUserWebDocId(null);
          setMessageNotifications([]);
        }
      }
    };

    setupMessageListener();

    return () => {
      cancelled = true;
      if (unsubscribeMessages) unsubscribeMessages();
      setUserWebDocId(null);
      setMessageNotifications([]);
    };
  }, [user?.uid]);

  useEffect(() => {
    const sellerIds = [...new Set([userWebDocId, user?.uid].filter(Boolean))];

    if (!sellerIds.length) {
      setResponsibleShipperUids([]);
      return undefined;
    }

    const db = firebase.firestore();

    const publishMergedShipperUids = () => {
      const merged = [
        ...new Set(Object.values(shipperIdsBySellerRef.current).flat()),
      ];
      setResponsibleShipperUids(merged);
    };

    const unsubscribes = sellerIds.map((sellerId) =>
      db
        .collection('shipper')
        .where('financial.sellerResponsible', '==', sellerId)
        .onSnapshot(
          (snapshot) => {
            shipperIdsBySellerRef.current[sellerId] = snapshot.docs.map(
              (doc) => doc.id,
            );
            publishMergedShipperUids();
          },
          (error) => {
            console.error(
              'Erro ao buscar embarcadores do vendedor responsável:',
              error,
            );
            shipperIdsBySellerRef.current[sellerId] = [];
            publishMergedShipperUids();
          },
        ),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      shipperIdsBySellerRef.current = {};
    };
  }, [userWebDocId, user?.uid]);

  useEffect(() => {
    setTransportQueueNotifications([]);

    if (!responsibleShipperUids.length) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;
    let inFlight = false;

    const refreshSellerTransportNotifications = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;

      const activeFreightIds = new Set();

      try {
        await Promise.all(
          responsibleShipperUids.map(async (shipperUid) => {
            try {
              const params = {
                uidShipper: shipperUid,
                limit: 100,
                status: TRANSPORT_QUEUE_FREIGHT_STATUSES,
              };

              if (user?.uid) {
                params.idUser = user.uid;
              }
              if (typeof user?.viewALL === 'boolean') {
                params.viewAll = String(user.viewALL);
              }
              if (
                Array.isArray(user?.uidShipperAffiliates) &&
                user.uidShipperAffiliates.length
              ) {
                params.affiliates = user.uidShipperAffiliates.join(',');
              }

              const response = await freightApi2.getAllFreightsV2(params);

              if (cancelled) return;

              const items = parseFreightsV2Items(response);
              for (const item of items) {
                const freightId = item?.id || item?._id;
                if (!freightId) continue;

                const freightData = mapApiFreightForNotification(item);
                if (!isFreightPendingContract(freightData)) continue;

                // Back exige isShipper = clientPayment.uid ou shipper.uid do frete.
                // Não usar o uid do vendedor responsável como fallback (gera 400 INVALID_SHIPPER).
                const paymentUid =
                  resolveShipperUidFromFreight(item) ||
                  resolveShipperUidFromFreight(freightData);
                if (!paymentUid) continue;

                activeFreightIds.add(freightId);
                await refreshTransportQueueForFreight(
                  freightId,
                  paymentUid,
                  freightData,
                );
              }
            } catch (error) {
              console.error(
                'Erro ao buscar fretes do vendedor responsável (API):',
                error,
              );
            }
          }),
        );

        if (cancelled) return;

        setTransportQueueNotifications((prev) =>
          prev.filter((notif) => activeFreightIds.has(notif.freightId)),
        );
      } finally {
        inFlight = false;
      }
    };

    // Adia o 1º poll para não dobrar /freight/v2 com a abertura da listagem.
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      refreshSellerTransportNotifications();
      intervalId = setInterval(
        refreshSellerTransportNotifications,
        TRANSPORT_QUEUE_POLL_MS,
      );
    }, TRANSPORT_QUEUE_INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [responsibleShipperUids, user?.viewALL, user?.uidShipperAffiliates]);

  useEffect(() => {
    if (!isPrincipalShipper) {
      cleanupQueueListeners();
      setQueueNotifications([]);
      return undefined;
    }

    const db = firebase.firestore();

    // Busca todos os fretes primeiro
    const unsubscribeFreights = db.collection('freight')
      .onSnapshot(async (freightSnapshot) => {
        cleanupQueueListeners();

        // Para cada frete, cria um listener na subcoleção queue
        freightSnapshot.docs.forEach(freightDoc => {
          const freightId = freightDoc.id;
          const freightData = freightDoc.data();
          
          // Verifica se o status do frete é pendente de validação ou contratação
          const isPendingValidation = isFreightPendingValidation(freightData);
          const freightStatus = freightData?.status?.describe || '';
          const isPendingContract = isPendingValidation ||
                                   freightStatus === 'P.Contratação' ||
                                   freightStatus === 'Pendente de contratação';
          
          // Só cria listener para fretes com status pendente de validação/contratação
          if (!isPendingContract) {
            return;
          }

          const unsubscribeQueue = db.collection('freight')
            .doc(freightId)
            .collection('queue')
            .onSnapshot((queueSnapshot) => {
              setQueueNotifications((prev) => {
                const filtered = prev.filter(
                  (notif) =>
                    notif.type !== 'queue' || notif.freightId !== freightId,
                );

                const newNotifications = [];

                queueSnapshot.docs.forEach((queueDoc) => {
                  const queueData = queueDoc.data();
                  const isRead = queueData.isRead === true;

                  if (!isRead) {
                    const origin =
                      freightData?.firstDelivery?.city || 'Origem não informada';
                    const destination =
                      freightData?.lastDelivery?.city || 'Destino não informado';
                    const driverName = queueData.driver_name || 'Motorista';

                    newNotifications.push({
                      id: `${freightId}_${queueDoc.id}`,
                      type: 'queue',
                      queueDocId: queueDoc.id,
                      freightId,
                      freightNumber: freightData?.numberSerial || freightId,
                      driverName,
                      driverId:
                        queueData.driver_user ||
                        queueData.uid ||
                        queueDoc.id ||
                        null,
                      origin,
                      destination,
                      title: `${driverName.split(' ')[0] || 'Motorista'} ${driverName.split(' ')[1] || ''} entrou na fila!`.trim(),
                      description: `${origin} → ${destination}`,
                      date: queueData.timestamp || new Date(),
                      read: isRead,
                    });
                  }
                });

                return [...filtered, ...newNotifications];
              });
            }, (error) => {
              console.error('Erro ao buscar fila:', error);
            });

          unsubscribeRefs.current.push(unsubscribeQueue);
        });
      }, (error) => {
        console.error('Erro ao buscar fretes:', error);
      });

    return () => {
      unsubscribeFreights();
      cleanupQueueListeners();
    };
  }, [isPrincipalShipper]);




  const calculateBackgroundColor = (date) => {
    //precisa retornar um boleano se a data for maior que uma hora
    if (!date) return false;
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const diff = differenceInHours(new Date(), dateObj);

    

    return diff > 1 ? true : false;
  };

  const notifications = useMemo(() => {
    return [
      ...queueNotifications,
      ...transportQueueNotifications,
      ...messageNotifications,
    ].sort((a, b) => toNotificationDate(b.date) - toNotificationDate(a.date));
  }, [queueNotifications, transportQueueNotifications, messageNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notificationId) => {
    const notification = notifications.find(
      (notif) => notif.id === notificationId,
    );

    if (!notification) return;

    if (notification.type === 'transportQueue') {
      handleClose();

      if (notification.freightId && notification.idTransport) {
        markTransportQueueNotificationRead(
          notification.freightId,
          notification.idTransport,
        );
        setTransportQueueNotifications((prev) =>
          prev.filter((notif) => notif.id !== notification.id),
        );
      }

      setTransportModalFreightId(notification.freightId);
      setTransportModalShipperUid(notification.shipperUid || '');
      setTransportModalFreightStatus(notification.freightStatus || null);
      setTransportActionMessage(null);

      try {
        const shipperForQueue = String(notification.shipperUid || '').trim();
        if (!shipperForQueue) {
          setTransportActionMessage({
            severity: 'error',
            text: 'Embarcador do frete não identificado para abrir a fila.',
          });
          setTransportsInFila([]);
          setTransportModalOpen(true);
          return;
        }

        const res = await freightApi2.transportQueue(
          notification.freightId,
          shipperForQueue,
        );
        setTransportsInFila(parseTransportQueueResponse(res));
        setTransportModalOpen(true);
      } catch (error) {
        console.error('Erro ao abrir fila de transportadoras:', {
          freightId: notification.freightId,
          shipperUid: notification.shipperUid,
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message,
        });
        setTransportActionMessage({
          severity: 'error',
          text: 'Não foi possível carregar a fila de transportadoras.',
        });
        setTransportsInFila([]);
        setTransportModalOpen(true);
      }
      return;
    }

    setSelectedNotification(notification);
    setOpenModal(true);
    handleClose();
  };

  const handleCloseTransportModal = () => {
    setTransportModalOpen(false);
    setTransportModalFreightId(null);
    setTransportModalShipperUid(null);
    setTransportModalFreightStatus(null);
    setTransportsInFila([]);
    setAcceptingTransportId(null);
    setCancelFreightLoading(false);
    setTransportActionMessage(null);
  };

  const handleCancelFreightFromQueue = async (reason) => {
    if (!transportModalFreightId || cancelFreightLoading) return;

    setCancelFreightLoading(true);

    try {
      await cancelFreightById(transportModalFreightId, reason, {
        idUser: user?.uid,
        viewAll: Boolean(user?.viewALL),
      });

      setTransportQueueNotifications((prev) =>
        prev.filter((notif) => notif.freightId !== transportModalFreightId),
      );

      setTransportActionMessage({
        severity: 'success',
        text: 'Frete cancelado com sucesso.',
      });
      handleCloseTransportModal();
    } catch (error) {
      console.error('Erro ao cancelar frete:', error);
      setTransportActionMessage({
        severity: 'error',
        text: getCancelFreightErrorMessage(
          error,
          'Não foi possível cancelar o frete.',
        ),
      });
    } finally {
      setCancelFreightLoading(false);
    }
  };

  const handleAcceptTransport = async (transport) => {
    if (
      !transportModalFreightId ||
      !transport?.idTransport ||
      acceptingTransportId
    ) {
      return;
    }

    setAcceptingTransportId(transport.idTransport);
    setTransportActionMessage(null);

    try {
      await acceptTransportFromQueue({
        freightId: transportModalFreightId,
        transport,
        user,
      });

      markTransportQueueNotificationRead(
        transportModalFreightId,
        transport.idTransport,
      );

      setTransportQueueNotifications((prev) =>
        prev.filter(
          (notif) =>
            !(
              notif.freightId === transportModalFreightId &&
              notif.idTransport === transport.idTransport
            ),
        ),
      );

      const shipperForQueue = String(transportModalShipperUid || '').trim();
      if (!shipperForQueue) {
        setTransportActionMessage({
          severity: 'error',
          text: 'Embarcador do frete não identificado para atualizar a fila.',
        });
        return;
      }

      const res = await freightApi2.transportQueue(
        transportModalFreightId,
        shipperForQueue,
      );
      const queue = parseTransportQueueResponse(res);
      setTransportsInFila(queue);

      if (!queue.length) {
        handleCloseTransportModal();
      }

      setTransportActionMessage({
        severity: 'success',
        text: 'Transportadora aceita com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao aceitar transportadora:', error);
      setTransportActionMessage({
        severity: 'error',
        text: getAcceptTransportErrorMessage(
          error,
          'Não foi possível aceitar a transportadora.',
        ),
      });
    } finally {
      setAcceptingTransportId(null);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedNotification(null);
  };

  const markNotificationAsRead = async (notification) => {
    if (!notification) return;

    const db = firebase.firestore();

    try {
      if (
        notification.type === 'message' &&
        notification.userWebDocId &&
        notification.messageDocId
      ) {
        await db
          .collection('user_web')
          .doc(notification.userWebDocId)
          .collection('message')
          .doc(notification.messageDocId)
          .update({ isRead: true });

        setMessageNotifications((prev) =>
          prev.filter((notif) => notif.id !== notification.id),
        );
      } else if (notification.freightId && notification.queueDocId) {
        await db
          .collection('freight')
          .doc(notification.freightId)
          .collection('queue')
          .doc(notification.queueDocId)
          .update({ isRead: true });

        setQueueNotifications((prev) =>
          prev.filter((notif) => notif.id !== notification.id),
        );
      } else if (
        notification.type === 'transportQueue' &&
        notification.freightId &&
        notification.idTransport
      ) {
        markTransportQueueNotificationRead(
          notification.freightId,
          notification.idTransport,
        );

        setTransportQueueNotifications((prev) =>
          prev.filter((notif) => notif.id !== notification.id),
        );
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleMarkAsRead = async () => {
    if (!selectedNotification) return;

    await markNotificationAsRead(selectedNotification);
    handleCloseModal();
  };

  const handleDriverClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedNotification?.driverId) {
      navigate(`/driverList/${selectedNotification.driverId}`);
      handleCloseModal();
    }
  };

  const handleFreightClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedNotification?.freightId) {
      await markNotificationAsRead(selectedNotification);
      navigate(`/freightCopy/${selectedNotification.freightId}`);
      handleCloseModal();
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return formatDistanceToNow(dateObj, { addSuffix: true, locale: ptBR });
    } catch (error) {
      return '';
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        style={{ color: '#FFF' }}
        aria-label="notifications"
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <NotificationsNoneOutlinedIcon color="#FFF" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'hidden',
            filter: 'drop-shadow(0px 8px 24px rgba(0,0,0,0.12))',
            mt: 1.5,
            minWidth: 380,
            maxWidth: 420,
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.08)',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              borderLeft: '1px solid rgba(0,0,0,0.08)',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '12px',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              bgcolor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#222',
                m: 0,
              }}
            >
              Notificações
            </Typography>
          </Box>

          {/* Notifications List */}
          <Box
            sx={{
              maxHeight: notifications.length > 5 ? '400px' : 'none',
              overflowY: notifications.length > 5 ? 'auto' : 'visible',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}
          >
            {notifications.length === 0 ? (
              <Box
                sx={{
                  px: 3,
                  py: 4,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#717171',
                    fontSize: '14px',
                  }}
                >
                  Nenhuma notificação no momento
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification.id)}
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderRadius: '20px',
                    marginBottom: '4px',
                    bgcolor: notification.read ? 'transparent' : calculateBackgroundColor(notification.date) ? '#FF2C2C' : '#FFF',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: notification.read ? '#fafafa' : '#f0f0f0',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor:
                        notification.type === 'message'
                          ? '#012442'
                          : notification.type === 'transportQueue'
                            ? '#012442'
                            : '#FD0100',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    {notification.type === 'message' ? (
                      <MailOutlineIcon sx={{ fontSize: 20 }} />
                    ) : notification.type === 'transportQueue' ? (
                      <FireTruckOutlinedIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <LocalShippingIcon sx={{ fontSize: 20 }} />
                    )}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: notification.read ? 500 : 600,
                        fontSize: '14px',
                        color: '#222',
                        mb: 0.5,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {truncateText(notification.title, 200)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        color: '#012442',
                        mb: 0.5,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {truncateText(notification.description, 500)}
                    </Typography>
                    {notification.date && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          color: '#000',
                          display: 'block',
                        }}
                      >
                        {formatTimeAgo(notification.date)}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Box>
      </Menu>

      {/* Modal de Detalhes da Notificação — visual alinhado à tela de frete */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: COLORS.paper,
            boxShadow: '0px 10px 40px rgba(1, 36, 67, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        {selectedNotification && (
          <>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${COLORS.line}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: COLORS.white,
              }}
            >
              <IconButton
                onClick={handleCloseModal}
                size="small"
                sx={{
                  color: COLORS.ink,
                  '&:hover': { bgcolor: 'rgba(1, 36, 66, 0.06)' },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  flex: 1,
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: COLORS.ink,
                  textAlign: 'center',
                  mr: 5,
                }}
              >
                Detalhes da notificação
              </Typography>
            </Box>

            <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: COLORS.paper }}>
              <Stack spacing={2.5}>
                {selectedNotification.type === 'message' ? (
                  <>
                    <Card sx={{ ...modalInfoCardSx, p: 2.5 }}>
                      <Typography
                        component="h4"
                        sx={{
                          alignItems: 'center',
                          color: COLORS.ink,
                          display: 'flex',
                          fontWeight: 700,
                          fontSize: '1rem',
                          gap: 1,
                          m: 0,
                          mb: 2,
                        }}
                      >
                        <MailOutlineIcon sx={{ color: COLORS.danger }} />
                        Mensagem
                      </Typography>
                      <Typography sx={{ ...modalValueSx, mb: 1.5 }}>
                        {selectedNotification.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          color: COLORS.inkSoft,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {selectedNotification.description || 'Sem conteúdo'}
                      </Typography>
                    </Card>

                    {selectedNotification.freightId && (
                      <Card sx={{ ...modalInfoCardSx, p: 2.5 }}>
                        <Typography
                          component="h4"
                          sx={{
                            alignItems: 'center',
                            color: COLORS.ink,
                            display: 'flex',
                            fontWeight: 700,
                            fontSize: '1rem',
                            gap: 1,
                            m: 0,
                            mb: 2,
                          }}
                        >
                          <FireTruckOutlinedIcon sx={{ color: COLORS.danger }} />
                          Frete
                        </Typography>
                        <Box
                          component={Link}
                          to={`/freightCopy/${selectedNotification.freightId}`}
                          onClick={handleFreightClick}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1.25,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: '12px',
                            bgcolor: COLORS.paper,
                            border: `1px solid ${COLORS.line}`,
                            textDecoration: 'none',
                            color: COLORS.ink,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: COLORS.white,
                              borderColor: 'rgba(255, 49, 0, 0.35)',
                            },
                          }}
                        >
                          <LocalShippingIcon sx={{ color: COLORS.danger, fontSize: 24 }} />
                          <Typography sx={modalValueSx}>
                            #{selectedNotification.freightNumber}
                          </Typography>
                        </Box>
                      </Card>
                    )}
                  </>
                ) : (
                  <>
                    <Card sx={{ ...modalInfoCardSx, p: 2.5 }}>
                      <Typography
                        component="h4"
                        sx={{
                          alignItems: 'center',
                          color: COLORS.ink,
                          display: 'flex',
                          fontWeight: 700,
                          fontSize: '1rem',
                          gap: 1,
                          m: 0,
                          mb: 2,
                        }}
                      >
                        <PersonIcon sx={{ color: COLORS.danger }} />
                        Motorista
                      </Typography>

                      <Box
                        component={selectedNotification.driverId ? Link : Box}
                        to={
                          selectedNotification.driverId
                            ? `/driverList/${selectedNotification.driverId}`
                            : '#'
                        }
                        onClick={handleDriverClick}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: '12px',
                          bgcolor: COLORS.paper,
                          border: `1px solid ${COLORS.line}`,
                          textDecoration: 'none',
                          color: 'inherit',
                          cursor: selectedNotification.driverId
                            ? 'pointer'
                            : 'default',
                          transition: 'all 0.2s ease',
                          '&:hover': selectedNotification.driverId
                            ? {
                                bgcolor: COLORS.white,
                                borderColor: 'rgba(255, 49, 0, 0.35)',
                              }
                            : {},
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: COLORS.danger,
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Typography sx={modalValueSx}>
                          {selectedNotification.driverName}
                        </Typography>
                      </Box>
                    </Card>

                    <Card sx={{ ...modalInfoCardSx, p: 2.5 }}>
                      <Typography
                        component="h4"
                        sx={{
                          alignItems: 'center',
                          color: COLORS.ink,
                          display: 'flex',
                          fontWeight: 700,
                          fontSize: '1rem',
                          gap: 1,
                          m: 0,
                          mb: 2,
                        }}
                      >
                        <FireTruckOutlinedIcon sx={{ color: COLORS.danger }} />
                        Frete
                      </Typography>
                      <Box
                        component={Link}
                        to={`/freightCopy/${selectedNotification.freightId}`}
                        onClick={handleFreightClick}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '12px',
                          bgcolor: COLORS.paper,
                          border: `1px solid ${COLORS.line}`,
                          textDecoration: 'none',
                          color: COLORS.ink,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: COLORS.white,
                            borderColor: 'rgba(255, 49, 0, 0.35)',
                          },
                        }}
                      >
                        <LocalShippingIcon sx={{ color: COLORS.danger, fontSize: 24 }} />
                        <Typography sx={modalValueSx}>
                          #{selectedNotification.freightNumber}
                        </Typography>
                      </Box>
                    </Card>

                    <Card sx={{ ...modalInfoCardSx, p: 2.5 }}>
                      <Typography
                        component="h4"
                        sx={{
                          alignItems: 'center',
                          color: COLORS.ink,
                          display: 'flex',
                          fontWeight: 700,
                          fontSize: '1rem',
                          gap: 1,
                          m: 0,
                          mb: 2,
                        }}
                      >
                        <NotificationsActiveOutlinedIcon sx={{ color: COLORS.danger }} />
                        Rota
                      </Typography>
                      <Box
                        sx={{
                          ...modalInfoCardSx,
                          bgcolor: COLORS.paper,
                          boxShadow: 'none',
                        }}
                      >
                        <Box sx={{ py: 0.5 }}>
                          <Typography sx={modalSectionLabelSx}>Origem</Typography>
                          <Typography sx={modalValueSx}>
                            {selectedNotification.origin}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1.5, borderColor: COLORS.line }} />
                        <Box sx={{ py: 0.5 }}>
                          <Typography sx={modalSectionLabelSx}>Destino</Typography>
                          <Typography sx={modalValueSx}>
                            {selectedNotification.destination}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </>
                )}
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: 'center',
                gap: 1.5,
                px: 2.5,
                pb: 2.5,
                pt: 0,
                bgcolor: COLORS.paper,
              }}
            >
              <Button
                onClick={handleCloseModal}
                sx={{
                  borderRadius: '50px',
                  color: COLORS.ink,
                  minWidth: 120,
                  px: 3,
                  py: 1.1,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(1, 36, 66, 0.06)' },
                }}
              >
                Fechar
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={handleMarkAsRead}
                sx={{
                  backgroundColor: COLORS.danger,
                  borderRadius: '50px',
                  color: COLORS.white,
                  minWidth: 160,
                  px: 3,
                  py: 1.1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#D62900' },
                }}
              >
                Marcar como lida
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <DialogFilaTransport
        transportsInFila={transportsInFila}
        openModal={transportModalOpen}
        handleClose={handleCloseTransportModal}
        acceptingTransportId={acceptingTransportId}
        onAcceptTransport={handleAcceptTransport}
        canAccept
        actionMessage={transportActionMessage}
        freightId={transportModalFreightId}
        freightStatus={transportModalFreightStatus}
        canCancelFreight={
          Boolean(user?.permissoes?.freight?.isEdit || user?.viewALL)
        }
        onCancelFreight={handleCancelFreightFromQueue}
        cancelLoading={cancelFreightLoading}
      />
    </>
  );
}


